import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { TableCellType } from "components/LazyTable";
import SortableTable from "components/SortableTable";
import BasicActionsThreeDotsMenu from "components/ThreeDots/BasicActionsThreeDotsMenu";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import {
  DeleteUserCredentialRequest,
  UserScopeRequest,
  ListResponseOfProfileCredentialModel,
  ProfileCredentialModel,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import CredentialDialog from "./CredentialDialog";

const LicensureBodyCell = ({ value }: { value: any }) => (
  <Tooltip title={value?.licensureBodyName ?? ""}>
    <span>{value?.licensureBodyAcronym ?? ""}</span>
  </Tooltip>
);

const CredentialTypeCell = ({
  value,
  licenseLabel,
}: {
  value: any;
  licenseLabel: string;
}) => {
  const label = value?.credentialType ?? "";
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      color={label === licenseLabel ? "success" : "warning"}
    />
  );
};

type CredentialDetailsAccordionProps = {
  userId: string;
  organizationId: string;
  setSnackbarMessage: (msg: SnackbarMessageProps) => void;
  onDataChanged: () => void;
};

const CredentialDetailsAccordion = ({
  userId,
  organizationId,
  setSnackbarMessage,
  onDataChanged,
}: CredentialDetailsAccordionProps) => {
  const { formatMessage: t } = useIntl();
  const { userProfileCredentialService } = useGlobalContext();

  const [expanded, setExpanded] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [
    credentialToEdit,
    setCredentialToEdit,
  ] = useState<ProfileCredentialModel | null>(null);
  const [
    credentialToDelete,
    setCredentialToDelete,
  ] = useState<ProfileCredentialModel | null>(null);

  const credentialsResult = useStrictQuery<ListResponseOfProfileCredentialModel>(
    ["getUserProfileCredentials", userId],
    () =>
      userProfileCredentialService.getUserCredentials(
        UserScopeRequest.fromJS({
          userId,
          organizationId,
        }),
      ),
    { enabled: expanded },
  );

  const credentials = credentialsResult.data?.data ?? [];

  const cells: Array<TableCellType> = [
    {
      accessor: "designationName",
      type: "text",
      label: t({ id: "designation" }),
    },
    {
      accessor: "locationName",
      type: "text",
      label: t({ id: "location" }),
    },
    {
      accessor: "licensureBodyAcronym",
      type: "node",
      label: t({ id: "licensureBody" }),
      View: LicensureBodyCell,
    },
    {
      accessor: "credentialType",
      type: "node",
      label: t({ id: "typeLabel" }),
      View: ({ value }: { value: any }) => (
        <CredentialTypeCell value={value} licenseLabel={t({ id: "license" })} />
      ),
    },
    {
      accessor: "credential",
      type: "node",
      align: "right",
      width: 80,
      label: "",
      View: ({ value }: { value: any }) => {
        const credential = value?.credential ?? value;
        const isLicense = !!credential?.licenseId;
        return (
          <BasicActionsThreeDotsMenu
            onEdit={
              isLicense
                ? () => {
                    setCredentialToEdit(credential);
                    setEditDialogOpen(true);
                  }
                : undefined
            }
            onDelete={() => setCredentialToDelete(credential)}
          />
        );
      },
    },
  ];

  const rows = credentials.map((c) => ({
    id: `${c.designation?.id}-${c.licenseId ?? "no-license"}`,
    designationName: c.designation?.name ?? "",
    locationName: c.licenseId ? c.location?.name ?? "" : "",
    licensureBodyAcronym: c.licensureBody?.acronym ?? "",
    licensureBodyName: c.licensureBody?.name ?? "",
    credentialType: c.licenseId
      ? t({ id: "license" })
      : t({ id: "designation" }),
    credential: c,
  }));

  const handleDelete = async () => {
    if (!credentialToDelete) return;
    try {
      await userProfileCredentialService.deleteUserCredential(
        DeleteUserCredentialRequest.fromJS({
          userId,
          organizationId,
          designationId: credentialToDelete.designation?.id,
          licenseId: credentialToDelete.licenseId,
        }),
      );
      onDataChanged();
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "credentialDeleted" })),
      );
    } catch {
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "errorOccurred" }), Severity.Error),
      );
    }
    setCredentialToDelete(null);
  };

  const handleSaved = () => {
    onDataChanged();
    setSnackbarMessage(new SnackbarMessageProps(t({ id: "credentialSaved" })));
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="credentialDetails" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            <Box mb={3} textAlign="right">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setAddDialogOpen(true)}
              >
                <T id="addCredential" />
              </Button>
            </Box>

            <SortableTable
              rows={rows}
              loading={credentialsResult.isFetching}
              dense
              cells={cells}
              tableRowPerPage={10}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      <DeleteEntityDialog
        dialogTitleKey="deleteCredentialTitle"
        rawContent={t(
          { id: "confirmDeleteCredential" },
          { designation: credentialToDelete?.designation?.name ?? "" },
        )}
        open={!!credentialToDelete}
        handleDelete={handleDelete}
        onCancel={() => setCredentialToDelete(null)}
      />

      <CredentialDialog
        open={addDialogOpen || editDialogOpen}
        userId={userId}
        organizationId={organizationId}
        userCredential={credentialToEdit}
        onClose={() => {
          setCredentialToEdit(null);
          setAddDialogOpen(false);
          setEditDialogOpen(false);
        }}
        onSaved={handleSaved}
      />
    </>
  );
};

export default CredentialDetailsAccordion;
